import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Collapse from '@mui/material/Collapse';
import InputBase from '@mui/material/InputBase';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import ArrowIcon from '@mui/icons-material/ArrowRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { GetAlertById, SendEmail, DeleteAlert } from '../../REST/alert';

const getRecipientLabel = (recipient) => {
  if (!recipient) return '';

  if (typeof recipient === 'string') {
    return recipient;
  }

  return (
    recipient.email ||
    recipient.name ||
    recipient.company ||
    recipient._id ||
    recipient.id ||
    ''
  );
};

const getRecipientInitial = (recipient) => {
  const label = getRecipientLabel(recipient);
  return label ? label.charAt(0).toUpperCase() : 'U';
};

const getRecipients = (item) => {
  if (!item) return [];

  if (Array.isArray(item.recipientIds) && item.recipientIds.length > 0) {
    return item.recipientIds;
  }

  if (Array.isArray(item.recipientEmails) && item.recipientEmails.length > 0) {
    return item.recipientEmails;
  }

  if (Array.isArray(item.recipients) && item.recipients.length > 0) {
    return item.recipients;
  }

  if (Array.isArray(item.userIds) && item.userIds.length > 0) {
    return item.userIds;
  }

  return [];
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return '';

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-US', {
    month: 'numeric',day: 'numeric',year: 'numeric',hour: 'numeric',minute: '2-digit',second: '2-digit',hour12: true
  });
};

export default function RecipeReviewCard({ open, handleClose, item = {} }) {
  const [expanded, setExpanded] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [alertDetails, setAlertDetails] = React.useState(item || {});
  const [recipientAnchorEl, setRecipientAnchorEl] = React.useState(null);

  React.useEffect(() => {
    if (!item?._id) {
      setAlertDetails(item || {});
      return;
    }

    setAlertDetails(item);

    GetAlertById(item._id)
      .then((resp) => {
        const data = resp?.data?.data || resp?.data || item;
        setAlertDetails(data);
      })
      .catch((err) => {
        console.error('Failed to load alert details:', err);
      });
  }, [item]);

  const currentItem = alertDetails || item || {};
  const recipients = getRecipients(currentItem);
  const firstRecipient = recipients[0];
  const remainingRecipients = recipients.slice(1);
  const formattedDate = formatDateTime(currentItem?.date || currentItem?.createdAt);

  const isRecipientPopoverOpen = Boolean(recipientAnchorEl);

  const handleMoreRecipientsOpen = (event) => {
    setRecipientAnchorEl(event.currentTarget);
  };

  const handleMoreRecipientsClose = () => {
    setRecipientAnchorEl(null);
  };

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  const handleEmail = () => {
    SendEmail({
      email,
      id: currentItem?._id || currentItem?.id
    })
      .then(() => {
        setEmail('');
        setExpanded(false);
      })
      .catch((err) => console.error(err));
  };

  const handleDelete = () => {
    DeleteAlert(currentItem?._id || currentItem?.id)
      .then(() => {
        handleClose();
      })
      .catch((err) => console.error(err));
  };

  const printElem = () => {
    const printContent = document.getElementById('printContent');

    if (!printContent) return false;

    const mywindow = window.open('', 'PRINT', 'height=600,width=900');

    mywindow.document.write('<html><head><title>' + document.title + '</title>');
    mywindow.document.write('</head><body>');
    mywindow.document.write(printContent.innerHTML);
    mywindow.document.write('</body></html>');

    mywindow.document.close();
    mywindow.focus();
    mywindow.print();
    mywindow.close();

    return true;
  };

  return (
    <Dialog
      fullScreen
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          bgcolor: '#f8f7fc',
          height: '100vh',
          overflow: 'hidden'
        }
      }}
    >
      <Box
        className="d-none"
        id="printContent"
        sx={{ display: 'none' }}
      >
        <h4>{currentItem?.subject}</h4>
        <h6>{formattedDate}</h6>
        <section>
          <article dangerouslySetInnerHTML={{ __html: currentItem?.message || '' }} />
        </section>
      </Box>

      <Box
        sx={{
          height: '100vh',
          bgcolor: '#f8f7fc',
          px: { xs: 1, sm: 1.5, md: 2 },
          py: { xs: 1, sm: 1.5 },
          overflow: 'hidden'
        }}
      >
        <Paper
          elevation={8}
          sx={{
            maxWidth: 1500,width: '100%',
            mx: 'auto',height: 'calc(100vh - 16px)',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: '#ffffff',
            border: '1px solid rgba(103, 58, 183, 0.10)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Gradient Header */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              background: 'linear-gradient(135deg, #7e57c2 0%, #673ab7 45%, #4f2ca3 100%)',
              borderBottom: '1px solid rgba(103, 58, 183, 0.20)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexShrink: 0,
              color: '#ffffff'
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#ffffff',
                  lineHeight: 1.2
                }}
              >
                {currentItem?.subject || 'Alert Preview'}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.82)',
                  mt: 0.4,
                  fontWeight: 500
                }}
              >
                Email preview
              </Typography>
            </Box>

            <IconButton
              onClick={handleClose}
              sx={{
                color: '#ffffff',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.14)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Recipient Row + Date Time */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
              bgcolor: '#ffffff',
              flexShrink: 0
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                flexWrap: 'wrap'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: '#151525',
                  mr: 0.5
                }}
              >
                Sent to:
              </Typography>

              {firstRecipient ? (
                <Chip
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: '#d9c6ff !important',
                        color: '#673ab7 !important',
                        fontWeight: 800
                      }}
                    >
                      {getRecipientInitial(firstRecipient)}
                    </Avatar>
                  }
                  label={getRecipientLabel(firstRecipient)}
                  sx={{
                    height: 38,
                    px: 0.6,
                    borderRadius: 2.5,
                    bgcolor: '#fbf8ff',
                    border: '1px solid #d9c6ff',
                    color: '#1f1f2f',
                    fontWeight: 600
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recipient information available
                </Typography>
              )}

              {remainingRecipients.length > 0 && (
                <>
                  <Chip
                    label={`+${remainingRecipients.length} more`}
                    onClick={handleMoreRecipientsOpen}
                    sx={{
                      height: 38,
                      borderRadius: 2.5,
                      bgcolor: '#f2eaff',
                      border: '1px solid #c7a8ff',
                      color: '#673ab7',
                      fontWeight: 800,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#eadcff'
                      }
                    }}
                  />

                  <Popover
                    open={isRecipientPopoverOpen}
                    anchorEl={recipientAnchorEl}
                    onClose={handleMoreRecipientsClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left'
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        p: 1.5,
                        borderRadius: 3,
                        minWidth: 520,
                        maxWidth: 760,
                        maxHeight: 260,
                        overflow: 'auto',
                        boxShadow: '0 14px 40px rgba(31, 41, 55, 0.18)',
                        border: '1px solid rgba(103, 58, 183, 0.18)',
                        bgcolor: '#ffffff'
                      }
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 1,
                        px: 0.5,
                        fontWeight: 800,
                        color: '#5f5875',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4
                      }}
                    >
                      Other Recipients
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                        maxWidth: 720
                      }}
                    >
                      {remainingRecipients.map((recipient, index) => (
                        <Chip
                          key={`${getRecipientLabel(recipient)}-${index}`}
                          avatar={
                            <Avatar
                              sx={{
                                bgcolor: '#d9c6ff !important',
                                color: '#673ab7 !important',
                                fontWeight: 800
                              }}
                            >
                              {getRecipientInitial(recipient)}
                            </Avatar>
                          }
                          label={getRecipientLabel(recipient)}
                          sx={{
                            height: 40,
                            px: 0.7,
                            borderRadius: 2.5,
                            bgcolor: '#fbf8ff',
                            border: '1px solid #d9c6ff',
                            color: '#1f1f2f',
                            fontWeight: 600,
                            maxWidth: 260,
                            '& .MuiChip-label': {
                              px: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Popover>
                </>
              )}
            </Box>

            {formattedDate && (
              <Chip
                icon={<AccessTimeIcon />}
                label={formattedDate}
                sx={{
                  height: 36,
                  borderRadius: 2.5,
                  bgcolor: '#f8f5ff',
                  border: '1px solid #e1d3ff',
                  color: '#5f5875',
                  fontWeight: 700,
                  '& .MuiChip-icon': {
                    color: '#673ab7'
                  }
                }}
              />
            )}
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              p: { xs: 1.5, sm: 2 },
              bgcolor: '#ffffff',
              overflow: 'hidden'
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                height: '100%',
                borderRadius: 2.5,
                p: { xs: 2, sm: 3 },
                bgcolor: '#ffffff',
                borderColor: 'rgba(103, 58, 183, 0.16)',
                boxShadow: '0 10px 40px rgba(31, 41, 55, 0.05)',
                overflow: 'auto'
              }}
            >
              <Box
                sx={{
                  color: '#111827',
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  fontSize: '15px',
                  lineHeight: 1.7,
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto'
                  },
                  '& table': {
                    maxWidth: '100%'
                  },
                  '& p': {
                    marginTop: '0.6em',
                    marginBottom: '0.6em'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: currentItem?.message || '' }}
              />
            </Paper>
          </Box>

          {/* Bottom Actions */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 1,
              borderTop: '1px solid rgba(15, 23, 42, 0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#ffffff',
              flexShrink: 0
            }}
          >
            <IconButton onClick={printElem}>
              <PrintIcon />
            </IconButton>

            <IconButton onClick={handleDelete}>
              <DeleteIcon />
            </IconButton>

            <IconButton onClick={handleExpandClick}>
              <ShareIcon />
            </IconButton>
          </Box>

          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                pb: 1.5,
                bgcolor: '#ffffff',
                flexShrink: 0
              }}
            >
              <Paper
                component="form"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleEmail();
                }}
                sx={{
                  p: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid rgba(103, 58, 183, 0.25)',
                  borderRadius: 2
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  value={email}
                  placeholder="Send Email"
                  onChange={(evt) => setEmail(evt.target.value)}
                />

                <IconButton color="primary" sx={{ p: '10px' }} type="submit">
                  <ArrowIcon />
                </IconButton>
              </Paper>
            </Box>
          </Collapse>
        </Paper>
      </Box>
    </Dialog>
  );
}